import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import color from '../../../../assets/css/colors';
import fontFamily from '../../../../assets/css/fonts';
import {
  Modal,
  Backdrop,
  Fade,
  Typography,
  Button,
  Divider,
  Box,
  Grid,
  InputLabel,
} from '@material-ui/core';
import IconButton, { IconButtons } from '../../../../components/Buttons/IconButton';
import {
  formatPbDate,
  formatQty,
  formatCurrency,
  formatBoolean,
  formatCurrencySymbol,
} from '../../../../lib/fmt';
import { listActivityReportDetails } from '../../../../services/ActivityService';
const DEFAULT_FIELD_SIZE = 3;

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1500,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 30px',
    maxHeight: 600,
    overflowY: 'auto',
    fontFamily: fontFamily.headers,
    color: color.default.main,
    '& p': {
      fontSize: 16,
      fontFamily: fontFamily.headers,
      fontWeight: 400,
      lineHeight: '26px',
      color: color.default.main,
      marginTop: 0,
    },
    '& hr': {
      margin: '30px 0px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: 25,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
  paper: {
    position: 'absolute',
  },
}));

const GridItemText = ({ label, value, type, fieldSize }) => {
  if (!fieldSize) {
    fieldSize = DEFAULT_FIELD_SIZE;
  }

  if (value === '<nil>') {
    value = '';
  }

  return (
    <Grid item key={label} xs={12} sm={fieldSize === 12 ? 12 : 6} md={fieldSize}>
      <Grid wrap="nowrap" container direction="column">
        <Grid item>
          <InputLabel shrink>{label}</InputLabel>
        </Grid>
        <Grid
          item
          style={{
            borderBottom: '1px solid gray',
            minHeight: 32,
            marginTop: 2,
            fontSize: 16,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {type === 'bool' ? formatBoolean(value) : value}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default function ActivityReportDetails({
  onClose: handleClose,
  open: isOpen,
  value,
}) {
  const classes = useStyles();
  const [data, setData] = React.useState({});

  React.useEffect(() => {
    async function getData() {
      try {
        const res = await listActivityReportDetails(value?.id, value?.archived);
        if (res) {
          setData(res);
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (value?.id !== 0) {
      getData();
    }
  }, [isOpen]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modal}
      open={isOpen}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <div className={classes.fadeModalFull}>
          <div className={classes.modalHeader}>
            <div>
              <Typography className={classes.textBold} variant="h3" gutterBottom mb={100}>
                Activity Details
              </Typography>
            </div>
            <div>
              <IconButtons>
                <IconButton
                  icon="close"
                  text="Close Modal"
                  onClick={handleClose}
                  color="inherit"
                />
              </IconButtons>
            </div>
          </div>
          <Box>
            <div className={classes.modalBody}>
              <Grid container spacing={2}>
                <GridItemText label="Accrual" value={data.accrual || ''} />
                <GridItemText label="ATS Identifier" value={data.atsIdentifier || ''} />
                <GridItemText label="Batch No" value={data.batchNo || ''} fieldSize={6} />
                <GridItemText label="Classification" value={data.classification || ''} />
                <GridItemText
                  label="Comparison Indicator"
                  value={data.comparisonIndicator || ''}
                  type="bool"
                />
                <GridItemText label="Contra Type" value={data.contraType || ''} />

                <GridItemText label="Coupon Rate" value={data.couponRate || ''} />
                <GridItemText label="Created By API" value={data.createdByApi || ''} />
                <GridItemText label="Cum Qty" value={formatQty(data.cumQty) || ''} />

                <GridItemText
                  label="Currency Fees"
                  value={formatCurrencySymbol(data.currencyFees, value?.currency) || ''}
                />
                <GridItemText
                  label="Currency Gross Amt"
                  value={
                    formatCurrencySymbol(data.currencyGrossAmt, value?.currency) || ''
                  }
                />
                <GridItemText
                  label="Delivered"
                  value={data.delivered || ''}
                  type="bool"
                />

                <GridItemText label="Discretion" value={data.discretion || ''} />
                <GridItemText
                  label="Entry Type Description"
                  value={data.entryTypeDescription || ''}
                />
                <GridItemText label="Euroclear ID" value={data.euroclearId || ''} />

                <GridItemText label="Exec ID" value={data.execId || ''} />
                <GridItemText label="Is Corp" value={data.isCorp || ''} type="bool" />
                <GridItemText label="Is MSRB" value={data.isMsrb || ''} type="bool" />
                <GridItemText
                  label="Is Open Item"
                  value={data.isOpenItem || ''}
                  type="bool"
                />
                <GridItemText
                  label="Is Solicited"
                  value={data.isSolicited || ''}
                  type="bool"
                />
                <GridItemText label="Leaves Qty" value={data.leavesQty || ''} />

                <GridItemText label="Leg ID" value={data.legId || ''} />
                <GridItemText
                  label="Mark Amt"
                  value={formatCurrency(data.markAmount) || ''}
                />
                <GridItemText label="Mark Rate" value={data.markRate || ''} />

                <GridItemText label="New Issue" value={data.newIssue || ''} type="bool" />
                <GridItemText
                  label="No Remuneration"
                  value={data.noRemuneration || ''}
                  type="bool"
                />
                <GridItemText label="Order Type" value={data.orderType || ''} />

                <GridItemText label="Org Term" value={formatPbDate(data.orgTerm) || ''} />
                <GridItemText label="Org Type" value={data.orgType || ''} />
                <GridItemText label="PMP" value={data.pmp || ''} />

                <GridItemText label="Product" value={data.product || ''} />
                <GridItemText label="Report ID" value={data.reportActivityId || ''} />
                <GridItemText label="S3" value={data.s3 || ''} type="bool" />

                <GridItemText
                  label="Step Indicator"
                  value={data.stepIndicator || ''}
                  type="bool"
                />

                <GridItemText
                  label="System Date"
                  value={formatPbDate(data.systemDate) || ''}
                />
                <GridItemText label="Tax Rate" value={data.taxRate || ''} />
                <GridItemText
                  label="When Issue"
                  value={data.whenIssue || ''}
                  type="bool"
                />

                <GridItemText label="YTC" value={data.ytc || ''} />
                <GridItemText label="YTM" value={data.ytm || ''} />
              </Grid>
              <Divider
                style={{
                  marginTop: 25,
                }}
              />
            </div>
            <div className={classes.modalFooter}>
              <div className={classes.grdCellNone} style={{ marginRight: 10 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleClose();
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </Box>
        </div>
      </Fade>
    </Modal>
  );
}
